import {useEffect, useState} from "react"
import {Box, CircularProgress, Stack, Typography, useTheme} from "@mui/joy"
import {useTranslation} from "react-i18next"
import {useApiService} from "../hooks/useApiService"
import {fetchFeaturedLeagues} from "../services/competitions"
import {LeagueResponse} from "../types/league"
import {CompetitionCrest} from "./CompetitionSearchField"
import {activePalette, applyTransparency} from "../theme"

export type FeaturedCompetitionsProps = {
    selected?: LeagueResponse
    onSelect: (league: LeagueResponse) => void
}

/**
 * The competitions put forward before anything is typed.
 *
 * Most visits are for one of a handful of competitions, so they are offered as something to tap
 * rather than something to spell.
 */
const FeaturedCompetitions = ({selected, onSelect}: FeaturedCompetitionsProps) => {
    const {t} = useTranslation()
    const apiService = useApiService()
    const palette = activePalette(useTheme())

    const [leagues, setLeagues] = useState<LeagueResponse[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let abandoned = false

        ;(async () => {
            try {
                const featured = await fetchFeaturedLeagues(() => apiService.client(true, false))
                if (!abandoned) setLeagues(featured)
            } catch (error) {
                // The search field above is still a way in, so a failure here is left silent
                if (!abandoned) setLeagues([])
            } finally {
                if (!abandoned) setLoading(false)
            }
        })()

        return () => {
            abandoned = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    if (loading)
        return <Stack direction={"row"} alignItems={"center"} gap={1}>
            <CircularProgress size={"sm"}/>
            <Typography level={"body-sm"}>{t("loadingCompetitions")}</Typography>
        </Stack>

    if (!leagues.length)
        return null

    return <Stack gap={1}>
        <Typography level={"body-xs"} textTransform={"uppercase"} letterSpacing={"0.1em"}>
            {t("popularCompetitions")}
        </Typography>

        <Box display={"grid"} gap={1}
             gridTemplateColumns={{xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))"}}>
            {leagues.map(league => {
                const picked = selected?.league?.id === league.league?.id

                return <Stack key={league.league?.id}
                              direction={"row"}
                              alignItems={"center"}
                              gap={1}
                              paddingX={1.5}
                              paddingY={1.25}
                              onClick={() => onSelect(league)}
                              sx={{
                                  cursor: "pointer",
                                  borderRadius: "12px",
                                  minWidth: 0,
                                  border: `1px solid ${picked
                                      ? palette.primary["600"]
                                      : "rgba(255,255,255,0.1)"}`,
                                  background: picked
                                      ? applyTransparency(palette.primary["500"], 0.18)
                                      : "rgba(255,255,255,0.03)",
                                  ":hover": {borderColor: palette.primary["600"]}
                              }}>
                    <CompetitionCrest league={league} size={28}/>
                    <Stack minWidth={0}>
                        <Typography level={"body-sm"} noWrap>{league.league?.name}</Typography>
                        {league.country?.name && <Typography level={"body-xs"} textColor={"text.tertiary"} noWrap>
                            {league.country.name}
                        </Typography>}
                    </Stack>
                </Stack>
            })}
        </Box>
    </Stack>
}

export default FeaturedCompetitions
